/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/
import { Inject, Injectable } from '@angular/core';
import { union } from 'lodash-es';
import Shepherd from 'shepherd.js';
import { TourDetailsService } from './tour-details.service';

/**
 * Service responsible to run and initialize different on-boarding tours. Also, updates appropriate on-boarding flags
 * for the account on tour completion.
 */
@Injectable({
  providedIn: 'root',
})
export class TourService {
  /**
   * Account prefs of the current user.
   */
  currentUserPrefs = this.BulbAuthService.currentUser.prefs;

  /**
   * Flag to decide if end tour confirmation dialog is to be shown.
   */
  isEndTourModal: boolean = false;

  /**
   * Flag to handle user navigation on clicking the link from the tour.
   */
  clickedOnLink: boolean = false;

  /**
   * Flag to handle cancel event for onboarding tour.
   */
  handleCancelEvent: boolean = true;

  /**
   * Flag to tell if the user is a switched user.
   */
  switchedUser = this.BulbAuthService.currentUser.hasRole('SWITCHED_USER');

  /**
   * flag for storing timestamp.
   */
  previousFocusTimeStamp = -1;

  /**
   * Shepherd tour object.
   */
  tourObject: Shepherd.Tour;

  constructor(
    private tourDetailsService: TourDetailsService,
    @Inject('BulbAuthService') private BulbAuthService: any,
    @Inject('BulbBrowserService') private BulbBrowserService: any,
    @Inject('BulbErrorService') private BulbErrorService: any,
    @Inject('BulbModalService') private BulbModalService: any,
    @Inject('Restangular') private Restangular: any,
  ) {
  }

  /**
   * Set up Shepherd Tour object with default tour options.
   * @param tourName name of the current tour
   */
  setUpShepherd(tourName: string) {
    return new Shepherd.Tour({
      tourName,
      defaultStepOptions: this.tourDetailsService.defaultStepOptions,
      keyboardNavigation: false,
      useModalOverlay: true,
    });
  }

  /**
   * Starts the main tour when user sees their own profile page. When this tour ends, the yellow suggestion badge for
   * the menu ‘hamburger’ icon is displayed, unless the user has already seen the menu portion of the tour yet.
   * That remains lit until the user clicks it, even if it is after the user closes the browser and returns.
   * Also, updates the "onboardingShowMainTour" flag to false on tour completion.
   */
  startMainTour() {
    const { onboardingShowMainTour } = this.BulbAuthService.currentUser.prefs.onBoarding;
    if (onboardingShowMainTour && !this.isTourActive() && !this.switchedUser) {
      this.tourObject = this.setUpShepherd('mainTour');
      this.tourObject.addSteps(this.tourDetailsService.createMainTourSteps(this.tourObject));
      this.tourObject.start();

      this.tourObject.on('complete', () => {
        this.currentUserPrefs.onBoarding.onboardingShowMainTour = false;
        this.updateAccountPrefs();
        this.destroyTourStepsOnComplete();
      });
      this.registerEventHandlers();
      this.confirmEndTour();
      this.accessibilityOnTour();
    }
  }

  /**
   * Starts the menu tour once the user clicks on the menu icon. When this portion of the tour ends,
   * the menu is closed, and the suggestion badge for the ‘create page’ button is displayed.
   * Also, updates the "onboardingShowMenuTour" flag to false on tour completion.
   */
  startMenuTour() {
    const { onboardingShowMenuTour } = this.BulbAuthService.currentUser.prefs.onBoarding;
    if (onboardingShowMenuTour && !this.isTourActive() && !this.switchedUser) {
      this.tourObject = this.setUpShepherd('menuTour');
      // Add tourCompletedStep to the steps if page tour has been completed by the user
      // @ts-ignore
      const isMobile = !Foundation.utils.is_large_up();
      if (!this.currentUserPrefs.onBoarding.onboardingShowPageTour) {
        this.tourObject.addSteps(union(
          this.tourDetailsService.createMenuTourSteps(isMobile, this.tourObject),
          // @ts-ignore
          [this.tourDetailsService.createTourCompletedStep(this.BulbAuthService.currentUser.firstName, this.tourObject)],
        ));
      } else {
        this.tourObject.addSteps(this.tourDetailsService.createMenuTourSteps(isMobile, this.tourObject));
      }
      this.tourObject.start();

      this.tourObject.on('complete', () => {
        window.menuTourDone();
        this.currentUserPrefs.onBoarding.onboardingShowMenuTour = false;
        this.updateAccountPrefs();
        this.destroyTourStepsOnComplete();
      });
      this.registerEventHandlers();
      this.confirmEndTour();
    }
  }

  /**
   * Starts the page intro tour the first time a user creates a page, this occurs even if the user has cancelled
   * the tour. Also, updates the "onboardingShowPageIntro" flag to false on tour completion.
   */
  startPageIntro() {
    const { onboardingShowPageIntro } = this.BulbAuthService.currentUser.prefs.onBoarding;
    if (onboardingShowPageIntro && !this.isTourActive() && !this.switchedUser) {
      this.tourObject = this.setUpShepherd('pageIntroTour');
      // @ts-ignore
      const isMobile = Foundation.utils.is_small_only();
      const ckeEnabled = this.BulbAuthService.currentUser.enabledFeatureFlags.find((element) => element === 'cke');
      this.tourObject.addSteps(this.tourDetailsService.createPageIntroSteps(isMobile, this.tourObject, ckeEnabled));
      this.tourObject.start();

      this.tourObject.on('complete', () => {
        this.currentUserPrefs.onBoarding.onboardingShowPageIntro = false;
        this.updateAccountPrefs();
        this.destroyTourStepsOnComplete();
        this.startPageTour();
      });
      this.registerEventHandlers();
      this.confirmEndTour();
    }
  }

  /**
   * Starts the page tour upon completion of the Page Intro tour, unless the tour has been cancelled.
   * Also, updates the "onboardingShowPageTour" flag to false on tour completion.
   */
  startPageTour() {
    const { onboardingShowPageTour } = this.BulbAuthService.currentUser.prefs.onBoarding;
    if (onboardingShowPageTour && !this.isTourActive() && !this.switchedUser) {
      this.tourObject = this.setUpShepherd('pageTour');
      // Add tourCompletedStep to the steps if menu tour has been completed by the user
      const isMobileDevice = this.BulbBrowserService.isMobile();
      // @ts-ignore
      const isMediumDevice = Foundation.utils.is_medium_up();
      if (!this.currentUserPrefs.onBoarding.onboardingShowMenuTour) {
        this.tourObject.addSteps(union(
          this.tourDetailsService.createPageTourSteps(isMobileDevice, isMediumDevice, this.tourObject),
          [this.tourDetailsService.createTourCompletedStep(this.BulbAuthService.currentUser.firstName, this.tourObject)],
        ));
      } else {
        this.tourObject.addSteps(this.tourDetailsService.createPageTourSteps(isMobileDevice, isMediumDevice, this.tourObject));
      }
      this.tourObject.start();

      this.tourObject.on('complete', () => {
        this.currentUserPrefs.onBoarding.onboardingShowPageTour = false;
        this.updateAccountPrefs();
        this.destroyTourStepsOnComplete();
      });
      this.registerEventHandlers();
      this.confirmEndTour();
    }
  }

  /**
   * Patches the on-boarding flags to reset the lags value to show the tour again.
   */
  resetTourFlags() {
    this.currentUserPrefs.onBoarding.onboardingShowMainTour = true;
    this.currentUserPrefs.onBoarding.onboardingShowMenuTour = true;
    this.currentUserPrefs.onBoarding.onboardingShowPageIntro = true;
    this.currentUserPrefs.onBoarding.onboardingShowPageTour = true;
    this.updateAccountPrefs();
  }

  /**
   * Handles the cancel event for the tour.
   */
  confirmEndTour() {
    this.tourObject.on('cancel', (event) => {
      this.updatePrefsOnEndTour();
    });
  }

  /**
   * Update account prefs and close the tour when user ends the tour manually.
   */
  private updatePrefsOnEndTour() {
    this.currentUserPrefs = this.BulbAuthService.currentUser.prefs;
    this.currentUserPrefs.onBoarding.onboardingShowPageIntro = false;
    this.currentUserPrefs.onBoarding.onboardingShowMainTour = false;
    this.currentUserPrefs.onBoarding.onboardingShowMenuTour = false;
    this.currentUserPrefs.onBoarding.onboardingShowPageTour = false;
    this.updateAccountPrefs();
    this.destroyTourStepsOnComplete();
  }

  /**
   * Calls account patch api to update on-boarding prefs for the account.
   */
  updateAccountPrefs() {
    this.Restangular.one('accounts', this.BulbAuthService.currentUser.id)
      .patch({ prefs: this.currentUserPrefs })
      .then((updatedAccount) => {
        this.BulbAuthService.setCurrentUser(updatedAccount);
      })
      .catch((error) => {
        this.BulbErrorService.error('Cannot update the user preferences', error);
      });
  }

  /**
   * Checks whether the tour is active.
   * @return true if the tour is active
   */
  isTourActive() {
    return this.tourObject && this.tourObject.isActive();
  }

  /**
   * Hide the tour by triggering the cancel event.
   */
  hideTour() {
    this.handleCancelEvent = false;
    this.tourObject.cancel();
  }

  /**
   * Click listener to handle the outside click event for the tour steps.
   * @param event outside click event for the step
   */
  registerClickListener = (event) => {
    this.clickedOnLink = (event.target && (event.target.id === 'bulbAnchorTag'));
  };

  /**
   * Focus event listener to handle focus event if the user navigates to other window.
   * @param event focus event
   */
  registerFocusBackListener = (event) => {
    // Focus listener is called twice in the Safari browser this is known bug
    // Handling it by checking browser type and timestamp. Ignoring the call whose timestamp is greater
    if (this.BulbBrowserService.isSafari()) {
      if (event.timeStamp) {
        if (this.previousFocusTimeStamp === -1) {
          this.previousFocusTimeStamp = event.timeStamp;
        } else if (this.previousFocusTimeStamp < event.timeStamp) {
          this.previousFocusTimeStamp = -1;
          return;
        }
      }
    }
    if (!this.tourObject.getCurrentStep().isOpen() || this.clickedOnLink) {
      this.clickedOnLink = false;
    }
  };

  /**
   * Registers the event handlers for click and focus events.
   */
  registerEventHandlers() {
    window.addEventListener('focus', this.registerFocusBackListener);
    document.addEventListener('click', this.registerClickListener);
  }

  /**
   * Destroys tour steps and also de-registers the event listeners on tour complete.
   */
  destroyTourStepsOnComplete() {
    document.removeEventListener('click', this.registerClickListener);
    window.removeEventListener('focus', this.registerFocusBackListener);
    // Shepherd doesn't clean up itself on complete
    this.tourObject = null;
  }

  /**
   * Adding the role="presentation" attribute on the header and footer of onboarding tour.
   * So that the screen readers doesn't read the banner and contentinfo on the tour.
   */
  accessibilityOnTour() {
    jQuery('.shepherd-header').attr('role', 'presentation');
    jQuery('.shepherd-footer').attr('role', 'presentation');
  }
}
